import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, X, Plus, Save } from 'lucide-react';
import { xamplinApi, PURPOSE_BY_ID, type XpBrandKit } from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { cn } from '@/lib/cn';
import { toast } from '@/components/ui/Toast';

type Tab = 'identity' | 'surveys' | 'look' | 'assets' | 'outputs' | 'preview';
const TABS: [Tab, string][] = [
  ['identity', 'Identity'],
  ['surveys', 'Surveys'],
  ['look', 'Look & feel'],
  ['assets', 'Asset library'],
  ['outputs', 'Outputs'],
  ['preview', 'Preview'],
];

/**
 * Brand Studio (build-v2 F3). Reads the BrandKit (M7) for a brand (CoCompany)
 * and exposes Identity (colors/logo/flavors), Surveys (CS-1 / RS-1 /
 * Stakeholder families) and a live CS-1 phone preview in the brand palette.
 * Editing/persistence of flavors + survey wiring lands with intake (F7).
 */
export function BrandStudioPage() {
  const { id } = useParams<{ id: string }>();
  const companyId = id ? Number(id) : undefined;
  const [tab, setTab] = useState<Tab>('identity');

  const kitQ = useQuery({
    queryKey: queryKeys.xamplin.brandKit(companyId),
    queryFn: () => xamplinApi.brandKit({ companyId }),
    enabled: companyId != null,
  });
  const kit = kitQ.data;

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Link
          to="/brands"
          className="grid h-8 w-8 place-items-center rounded-lg border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <span
          className="grid h-10 w-10 place-items-center rounded-xl text-base font-extrabold text-white"
          style={{ background: kit?.secondaryColor || 'var(--color-xk-accent)' }}
        >
          {(kit?.name ?? '?').slice(0, 1).toUpperCase()}
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            {kit?.name ?? 'Brand Studio'}
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            Brand Studio
          </p>
        </div>
      </div>

      {kitQ.isLoading && (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando BrandKit…</div>
      )}
      {kitQ.isError && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudo cargar el BrandKit de esta marca.
        </div>
      )}

      {kit && (
        <>
          <div className="flex flex-wrap gap-1 border-b border-[var(--color-xk-border)]">
            {TABS.map(([t, label]) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  'border-b-2 px-4 py-2 text-sm font-semibold transition-colors',
                  tab === t
                    ? 'border-[var(--color-xk-accent)] text-[var(--color-xk-accent)]'
                    : 'border-transparent text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <div>
              {tab === 'identity' && <IdentityTab kit={kit} companyId={companyId!} />}
              {tab === 'surveys' && <SurveysTab kit={kit} companyId={companyId!} />}
              {tab === 'look' && <LookTab kit={kit} />}
              {tab === 'assets' && <AssetsTab companyId={companyId!} />}
              {tab === 'outputs' && <OutputsTab />}
              {tab === 'preview' && <PhonePreview kit={kit} />}
            </div>
            {/* Persistent phone preview on wide screens */}
            <div className="hidden lg:block">
              <PhonePreview kit={kit} compact />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function IdentityTab({ kit, companyId }: { kit: XpBrandKit; companyId: number }) {
  const qc = useQueryClient();
  // Local edit buffer, seeded from the resolved kit.
  const [bg, setBg] = useState(kit.backgroundColor ?? '');
  const [font, setFont] = useState(kit.fontColor ?? '');
  const [accent, setAccent] = useState(kit.secondaryColor ?? '');
  const [logo, setLogo] = useState(kit.logo ?? '');
  const [flavors, setFlavors] = useState<string[]>(kit.productVariants ?? []);
  const [competitive, setCompetitive] = useState<string[]>(kit.competitiveSet ?? []);

  const dirty =
    bg !== (kit.backgroundColor ?? '') ||
    font !== (kit.fontColor ?? '') ||
    accent !== (kit.secondaryColor ?? '') ||
    logo !== (kit.logo ?? '') ||
    JSON.stringify(flavors) !== JSON.stringify(kit.productVariants ?? []) ||
    JSON.stringify(competitive) !== JSON.stringify(kit.competitiveSet ?? []);

  const saveM = useMutation({
    mutationFn: () =>
      xamplinApi.brandKitUpdate(companyId, {
        backgroundColor: bg || null,
        fontColor: font || null,
        secondaryColor: accent || null,
        logo: logo || null,
        productVariants: flavors,
        competitiveSet: competitive,
      }),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Identidad guardada' });
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.brandKit(companyId) });
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo guardar' }),
  });

  const colors: Array<[string, string, (v: string) => void]> = [
    ['Background', bg, setBg],
    ['Font', font, setFont],
    ['Accent / primary', accent, setAccent],
  ];

  return (
    <div className="flex flex-col gap-5">
      <Section title="Colors">
        <div className="flex flex-wrap gap-4">
          {colors.map(([label, value, set]) => (
            <div key={label} className="flex flex-col items-center gap-1.5">
              <input
                type="color"
                value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'}
                onChange={(e) => set(e.target.value)}
                className="h-12 w-12 cursor-pointer rounded-xl border border-[var(--color-xk-border)] bg-transparent"
                aria-label={label}
              />
              <span className="mono text-[10px] text-[var(--color-xk-text-muted)]">{label}</span>
              <input
                value={value}
                onChange={(e) => set(e.target.value)}
                placeholder="#______"
                className="mono w-20 rounded border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-1.5 py-0.5 text-center text-[9px] text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]"
              />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Logo (URL)">
        <input
          value={logo}
          onChange={(e) => setLogo(e.target.value)}
          placeholder="https://…"
          className="w-full max-w-md rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-3 py-2 text-sm text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]"
        />
      </Section>

      <Section title="Flavors / sub-brands">
        <ChipEditor
          items={flavors}
          setItems={setFlavors}
          placeholder="Piña, Strawberry, Mango…"
          tone="lavender"
        />
      </Section>

      <Section title="Competitive set" hint="decoys CS-1">
        <ChipEditor
          items={competitive}
          setItems={setCompetitive}
          placeholder="Marca competidora…"
          tone="muted"
        />
      </Section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || saveM.isPending}
          onClick={() => saveM.mutate()}
          className="flex items-center gap-2 rounded-lg bg-[var(--color-xk-accent)] px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
        >
          <Save className="h-4 w-4" /> {saveM.isPending ? 'Guardando…' : 'Guardar identidad'}
        </button>
        {dirty && <span className="mono text-[11px] text-[var(--color-xk-text-muted)]">cambios sin guardar</span>}
      </div>
    </div>
  );
}

/** Add/remove chip editor for string lists (flavors, competitive set). */
function ChipEditor({
  items,
  setItems,
  placeholder,
  tone,
}: {
  items: string[];
  setItems: (next: string[]) => void;
  placeholder: string;
  tone: 'lavender' | 'muted';
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || items.includes(v)) return;
    setItems([...items, v]);
    setDraft('');
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {items.map((v) => (
          <span
            key={v}
            className={cn(
              'flex items-center gap-1 rounded-full border px-3 py-1 text-sm',
              tone === 'lavender'
                ? 'border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] text-[var(--color-xk-lavender)]'
                : 'mono border-[var(--color-xk-border)] text-xs text-[var(--color-xk-text-muted)]',
            )}
          >
            {v}
            <button
              type="button"
              onClick={() => setItems(items.filter((x) => x !== v))}
              className="text-[var(--color-xk-text-subtle)] hover:text-[var(--color-error)]"
              aria-label={`Quitar ${v}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {items.length === 0 && (
          <span className="text-sm text-[var(--color-xk-text-muted)]">Ninguno aún.</span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="w-full max-w-xs rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-3 py-1.5 text-sm text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]"
        />
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 rounded-lg border border-[var(--color-xk-accent)] px-2.5 py-1.5 text-sm font-semibold text-[var(--color-xk-accent)] hover:bg-[var(--color-accent-10)]"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

interface SurveyRow { label: string; desc: string; on: boolean; locked: boolean }
const SURVEY_FAMILIES: { id: string; name: string; target: string; rows: SurveyRow[] }[] = [
  {
    id: 'cs1',
    name: 'CS-1 — Consumer survey',
    target: 'Consumidor en la estación · QR',
    rows: [
      { label: 'Consent & age gate (21+)', desc: 'Requerido en activaciones con alcohol · bilingüe', on: true, locked: false },
      { label: 'Demographics', desc: 'Mujer-first · detalle hispano', on: true, locked: false },
      { label: '¿Probaste el producto?', desc: 'Trial validation · no se puede desactivar', on: true, locked: true },
      { label: 'Rating 1-5', desc: 'Captura enjoyment', on: true, locked: false },
      { label: 'Preference shift', desc: 'Pre/post · "¿cuál elegirías la próxima vez?"', on: true, locked: false },
      { label: 'Email opt-in (DiD)', desc: 'Enterprise · recheck 90 días', on: false, locked: false },
      { label: 'Bilingüe EN/ES', desc: 'Default ES en venues hispano-codificados', on: true, locked: false },
    ],
  },
  {
    id: 'rs1',
    name: 'RS-1 — Reporter family',
    target: 'BA / Field Lead / Venue Stakeholder',
    rows: [
      { label: 'Clock-in / clock-out', desc: 'Time-stamp en la station', on: true, locked: true },
      { label: 'Setup verification', desc: 'Foto + checklist por asset', on: true, locked: true },
      { label: 'Asset condition report', desc: 'New / used / damaged → warehouse', on: true, locked: true },
      { label: 'Foot-traffic estimate', desc: 'Observación del BA', on: true, locked: false },
      { label: 'Operational issues', desc: 'Texto + foto · escala a manager', on: true, locked: false },
    ],
  },
  {
    id: 'stake',
    name: 'Stakeholder sign-off',
    target: 'Contacto del venue (post-evento)',
    rows: [
      { label: '¿Coincidió con el brief?', desc: 'Locked · validación core', on: true, locked: true },
      { label: 'Operational concerns', desc: 'Conducta BA · ruido · limpieza', on: true, locked: false },
      { label: '¿Recibirías la marca de nuevo?', desc: 'Net welcome score', on: true, locked: false },
      { label: 'Nota libre a la marca', desc: 'Aparece anonimizada en el recap', on: true, locked: false },
    ],
  },
];

function SurveysTab({ kit, companyId }: { kit: XpBrandKit; companyId: number }) {
  const qc = useQueryClient();
  const defaults = () => {
    const init: Record<string, boolean> = {};
    SURVEY_FAMILIES.forEach((f) => f.rows.forEach((r, i) => (init[`${f.id}-${i}`] = r.on)));
    return init;
  };
  const [config, setConfig] = useState<Record<string, boolean>>(() => ({
    ...defaults(),
    ...(kit.surveyConfig ?? {}),
  }));
  const dirty = JSON.stringify(config) !== JSON.stringify({ ...defaults(), ...(kit.surveyConfig ?? {}) });

  const saveM = useMutation({
    mutationFn: () => xamplinApi.brandKitUpdate(companyId, { surveyConfig: config }),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Config de surveys guardada' });
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.brandKit(companyId) });
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo guardar' }),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[var(--color-xk-text-muted)]">
          Tres familias coordinadas vía Xurvey. Activa/desactiva preguntas; las bloqueadas son core.
        </p>
        <div className="flex flex-none items-center gap-2">
          <button
            type="button"
            disabled={!dirty || saveM.isPending}
            onClick={() => saveM.mutate()}
            className="mono rounded-lg bg-[var(--color-xk-accent)] px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-40"
          >
            {saveM.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <Link
            to="/surveys/builder"
            className="mono rounded-lg border border-[var(--color-xk-accent)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-xk-accent)] hover:bg-[var(--color-accent-10)]"
          >
            Abrir builder →
          </Link>
        </div>
      </div>
      {SURVEY_FAMILIES.map((f) => (
        <div key={f.id} className="rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4">
          <div className="mb-1 text-sm font-bold text-[var(--color-xk-lavender)]">{f.name}</div>
          <div className="mono mb-3 text-[11px] text-[var(--color-xk-text-muted)]">{f.target}</div>
          <div className="flex flex-col divide-y divide-[var(--color-xk-border)]">
            {f.rows.map((r, i) => {
              const key = `${f.id}-${i}`;
              const on = config[key];
              return (
                <div key={key} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-[var(--color-xk-lavender)]">
                      {r.label}
                      {r.locked && <span className="ml-1 text-[var(--color-xk-accent)]">🔒</span>}
                    </div>
                    <div className="mono text-[10px] text-[var(--color-xk-text-muted)]">{r.desc}</div>
                  </div>
                  <button
                    type="button"
                    disabled={r.locked}
                    onClick={() => setConfig((c) => ({ ...c, [key]: !c[key] }))}
                    className={cn(
                      'relative h-5 w-9 flex-none rounded-full transition-colors disabled:opacity-60',
                      on ? 'bg-[var(--color-xk-accent)]' : 'bg-[var(--color-xk-border)]',
                    )}
                    aria-pressed={on}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
                        on ? 'left-[18px]' : 'left-0.5',
                      )}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Look & feel — brand palette applied to sample UI (prototype LookTab). */
function LookTab({ kit }: { kit: XpBrandKit }) {
  const accent = kit.secondaryColor || kit.primaryColor || 'var(--color-xk-accent)';
  const bg = kit.backgroundColor || '#fff5ec';
  const font = kit.fontColor || '#4a1414';
  return (
    <div className="flex flex-col gap-5">
      <Section title="Componentes con la marca">
        <div className="flex flex-wrap items-center gap-3">
          <button className="rounded-full px-4 py-2 text-sm font-bold text-white" style={{ background: accent }}>
            Primary CTA
          </button>
          <button className="rounded-full border px-4 py-2 text-sm font-bold" style={{ borderColor: accent, color: accent }}>
            Secondary
          </button>
          <span className="mono rounded-full px-2.5 py-1 text-[10px] font-bold text-white" style={{ background: accent }}>
            BADGE
          </span>
        </div>
      </Section>
      <Section title="Card de muestra">
        <div className="max-w-sm rounded-2xl border p-5" style={{ background: bg, color: font, borderColor: accent + '55' }}>
          <div className="text-xs font-bold uppercase tracking-wider" style={{ opacity: 0.7 }}>{kit.name}</div>
          <div className="mt-1 text-lg font-extrabold">¿Quieres probar algo nuevo hoy?</div>
          <div className="mt-1 text-sm" style={{ opacity: 0.8 }}>6 preguntas · 30 segundos</div>
          <button className="mt-3 rounded-full px-4 py-2 text-sm font-bold text-white" style={{ background: accent }}>
            Empezar
          </button>
        </div>
      </Section>
    </div>
  );
}

/** Asset library summary by Purpose (prototype AssetsTabSummary). */
function AssetsTab({ companyId }: { companyId: number }) {
  const assetsQ = useQuery({ queryKey: queryKeys.xamplin.assets, queryFn: () => xamplinApi.assets.list(300) });
  const assets = (assetsQ.data?.items ?? []).filter((a) => a.image);
  const counts: Record<string, number> = { SEE: 0, ENGAGE: 0, TRY: 0, ACT: 0 };
  for (const a of assets) {
    const p = a.assetObjetive ? PURPOSE_BY_ID[a.assetObjetive] : null;
    if (p) counts[p]++;
  }
  const COLOR: Record<string, string> = { SEE: '#5aa9ff', ENGAGE: '#f4b740', TRY: '#4dd28b', ACT: '#ff5a4f' };
  void companyId;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(['SEE', 'ENGAGE', 'TRY', 'ACT'] as const).map((p) => (
          <div key={p} className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-3">
            <div className="font-display text-2xl font-extrabold" style={{ color: COLOR[p] }}>{counts[p]}</div>
            <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">{p}</div>
          </div>
        ))}
      </div>
      <Link to="/assets" className="mono w-fit rounded-lg border border-[var(--color-xk-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-xk-lavender)] hover:border-[var(--color-xk-accent)]">
        Ver biblioteca completa →
      </Link>
    </div>
  );
}

/** Outputs the brand produces (prototype OutputsTab). */
function OutputsTab() {
  const outputs = [
    { label: 'Recap deck', desc: '11 slides para el cliente', to: '/insights' },
    { label: 'Insights cross-cut', desc: 'MENT · funnel · DiD · stakeholder', to: '/insights' },
    { label: 'CSV / export', desc: 'Datos crudos de respuestas', to: '/insights' },
    { label: 'QR de survey', desc: 'Por station · CS-1', to: '/projects' },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {outputs.map((o) => (
        <Link
          key={o.label}
          to={o.to}
          className="flex items-center gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 transition-colors hover:border-[var(--color-xk-accent)]"
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-[var(--color-xk-lavender)]">{o.label}</div>
            <div className="mono text-[11px] text-[var(--color-xk-text-muted)]">{o.desc}</div>
          </div>
          <span className="text-[var(--color-xk-text-muted)]">→</span>
        </Link>
      ))}
    </div>
  );
}

function PhonePreview({ kit, compact }: { kit: XpBrandKit; compact?: boolean }) {
  const bg = kit.backgroundColor || '#fff5ec';
  const accent = kit.primaryColor || kit.secondaryColor || '#ff6b9d';
  const fontColor = kit.fontColor || '#4a1414';
  return (
    <div className={cn('flex flex-col items-center gap-2', !compact && 'mt-2')}>
      <div
        className="flex w-[280px] flex-col gap-4 rounded-[28px] border-4 border-[var(--color-xk-border-strong)] p-5"
        style={{ background: bg, color: fontColor, minHeight: 460 }}
      >
        <div className="text-center text-xs font-bold uppercase tracking-wider" style={{ opacity: 0.7 }}>
          {kit.name}
        </div>
        <div className="text-lg font-extrabold leading-tight">¿Quieres probar algo nuevo hoy?</div>
        <div className="text-sm" style={{ opacity: 0.8 }}>
          Cuéntanos tu experiencia — 6 preguntas, 30 segundos.
        </div>
        <div className="mt-auto flex flex-col gap-2">
          <button
            className="rounded-full py-3 text-sm font-bold text-white"
            style={{ background: accent }}
          >
            Empezar
          </button>
          <button
            className="rounded-full border py-3 text-sm font-bold"
            style={{ borderColor: accent, color: accent, background: 'transparent' }}
          >
            Tal vez después
          </button>
        </div>
        <div className="text-center text-[9px]" style={{ opacity: 0.5 }}>
          powered by onxite
        </div>
      </div>
      <span className="mono text-[10px] text-[var(--color-xk-text-subtle)]">CS-1 · preview</span>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
          {title}
        </h3>
        {hint && (
          <span className="mono text-[10px] text-[var(--color-xk-accent)]">· {hint}</span>
        )}
      </div>
      {children}
    </div>
  );
}
